import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Shield, Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/features", label: "Features" },
  { to: "/detection-catalog", label: "Detection Catalog" },
  { to: "/how-it-works", label: "How It Works" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#0a0a0a]/95 backdrop-blur-sm border-b border-[#1a1a1a]" : "bg-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2 group">
          <Shield size={16} strokeWidth={1.5} className="text-[#f59e0b]" />
          <span className="font-mono text-sm font-semibold tracking-tight text-[#ededed]">
            blackwall
          </span>
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-0.5">
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.label.toLowerCase().replace(/ /g, "-")}`}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                location.pathname === link.to
                  ? "text-[#ededed]"
                  : "text-[#71717a] hover:text-[#a1a1aa]"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href="#install"
            data-testid="navbar-install-cta"
            className="px-4 py-1.5 text-sm bg-[#ededed] text-[#0a0a0a] font-medium rounded-full hover:bg-white transition-colors"
          >
            Install
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="navbar-mobile-toggle"
          className="md:hidden text-[#71717a] hover:text-[#ededed] transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden bg-[#0a0a0a] border-b border-[#1a1a1a] px-6 py-4 space-y-1">
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`block py-2.5 text-sm border-b border-[#1a1a1a] last:border-0 ${
                location.pathname === link.to ? "text-[#ededed]" : "text-[#71717a]"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <a
            href="#install"
            className="block mt-4 px-4 py-2 text-sm bg-[#ededed] text-[#0a0a0a] font-medium rounded-full text-center"
          >
            Install Now
          </a>
        </div>
      )}
    </nav>
  );
}
